import styled from "@emotion/styled";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Toolbar,
  Typography,
} from "@mui/material";
import _ from "lodash";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Manufacturers,
  PremiumCalculationType,
  useProductTablePage_DeleteProductMutation,
  useProductTablePage_ProductsQuery,
} from "../../../../generated/graphql";
import { TabPanel } from "../../../components/TabPanel";
import { useState } from "react";
import DeleteModal from "../../../../components/DeleteModal";
import { GroupDropdown } from "../../../components/GroupDropdown";
import { DealershipDropdown } from "../../../components/DealershipDropdown";
import { useTenant } from "../../../context/TenantContext";

type StatusFilter = "active" | "inactive" | "all";

const Container = styled("div")`
  margin: 32px;
`;

const CToolbar = styled(Toolbar)`
  border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
`;

const Title = styled(Typography)`
  flex: 1;
`;

export const ProductTablePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = searchParams.get("manufacturer") as Manufacturers;
  const premiumCalculationType = searchParams.get(
    "premiumCalculationType"
  ) as Manufacturers;
  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={selectedTab}
          onChange={(_, value) => {
            setSearchParams({ manufacturer: value, premiumCalculationType });
          }}
        >
          <Tab label="Ravpro" value={Manufacturers.Ravpro} />
          <Tab label="Original" value={Manufacturers.Original} />
          <Tab label="Outros" value={Manufacturers.Other} />
        </Tabs>
      </Box>
      <TabPanel value={selectedTab} tab={Manufacturers.Ravpro}>
        <ProductTabs tab={selectedTab} />
      </TabPanel>
      <TabPanel value={selectedTab} tab={Manufacturers.Original}>
        <ProductTabs tab={selectedTab} />
      </TabPanel>
      <TabPanel value={selectedTab} tab={Manufacturers.Other}>
        <ProductTabs tab={selectedTab} />
      </TabPanel>
    </>
  );
};

export const ProductTabs = ({ tab }: { tab: Manufacturers }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const premiumCalculationType = searchParams.get(
    "premiumCalculationType"
  ) as PremiumCalculationType;
  const manufacturer = searchParams.get("manufacturer") as Manufacturers;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");

  const navigate = useNavigate();

  return (
    <Container>
      <GroupDropdown />
      <DealershipDropdown />
      <FormControl sx={{ width: "100%", marginBottom: 2 }}>
        <InputLabel id="status-filter-label">Status</InputLabel>
        <Select
          labelId="status-filter-label"
          id="status-filter"
          value={statusFilter}
          label="Status"
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
        >
          <MenuItem value="active">Ativo</MenuItem>
          <MenuItem value="inactive">Inativo</MenuItem>
          <MenuItem value="all">Todos</MenuItem>
        </Select>
      </FormControl>
      <Tabs
        value={premiumCalculationType}
        onChange={(_, value) => {
          setSearchParams({ premiumCalculationType: value, manufacturer });
        }}
      >
        <Tab label="Venda" value={PremiumCalculationType.Sale} />
        <Tab label="Pedido (VHSYS)" value={PremiumCalculationType.OrderVhsys} />
      </Tabs>
      <Paper>
        <CToolbar>
          <Title>Catálogos</Title>
          <IconButton
            onClick={() =>
              navigate(
                `/ravpro/products/create?manufacturer=${manufacturer}&premiumCalculationType=${premiumCalculationType}`
              )
            }
          >
            <Icon>add</Icon>
          </IconButton>
        </CToolbar>
        <TabPanel
          value={premiumCalculationType}
          tab={PremiumCalculationType.Sale}
        >
          <ProductsTable
            premiumCalculationType={premiumCalculationType}
            manufacturer={tab}
            statusFilter={statusFilter}
          />
        </TabPanel>
        <TabPanel
          value={premiumCalculationType}
          tab={PremiumCalculationType.OrderVhsys}
        >
          <ProductsTable
            premiumCalculationType={premiumCalculationType}
            manufacturer={tab}
            statusFilter={statusFilter}
          />
        </TabPanel>
      </Paper>
    </Container>
  );
};

type ProductsTableProps = {
  manufacturer: Manufacturers;
  premiumCalculationType: PremiumCalculationType;
  statusFilter: StatusFilter;
};

const ProductsTable = ({
  premiumCalculationType,
  manufacturer,
  statusFilter,
}: ProductsTableProps) => {
  const { groupId, dealershipId } = useTenant();
  const navigate = useNavigate();

  const [deleteId, setDeleteId] = useState<string | undefined>();
  const [deleteProduct, { loading: deleteLoading }] =
    useProductTablePage_DeleteProductMutation();

  // Converte o filtro de status para o valor booleano esperado pela API
  const activeFilter =
    statusFilter === "all" ? undefined : statusFilter === "active";

  const { data, refetch } = useProductTablePage_ProductsQuery({
    variables: {
      Manufacturer: manufacturer,
      groupId: groupId!,
      dealershipId: dealershipId ?? undefined,
      PremiumCalculationType: premiumCalculationType,
      active: activeFilter,
    },
    skip: !groupId,
  });
  return (
    <>
      <Table>
        {data?.products && data?.products.length > 0 ? (
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Código</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>
        ) : null}
        <TableBody>
          {_.orderBy(data?.products, "name").map((row) => (
            <TableRow key={row.uuid} hover>
              <TableCell>{row.ProductCatalog?.name}</TableCell>
              <TableCell>{row.productCode}</TableCell>
              <TableCell>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={row.active}
                      disabled
                      size="small"
                    />
                  }
                  label="Ativo"
                  sx={{ 
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      color: row.active ? 'success.main' : 'text.disabled'
                    }
                  }}
                />
              </TableCell>
              <TableCell align="right">
                <IconButton
                  onClick={() =>
                    navigate(
                      `/ravpro/products/update/${row.uuid}?manufacturer=${manufacturer}&premiumCalculationType=${premiumCalculationType}`
                    )
                  }
                >
                  <Icon>edit</Icon>
                </IconButton>
              </TableCell>
              <TableCell>
                <IconButton
                  onClick={() => {
                    setDeleteId(row.uuid);
                  }}
                >
                  <Icon>delete</Icon>
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {data?.products && data.products.length === 0 ? (
            <TableRow>
              <TableCell align="center" colSpan={5}>Nenhum registro encontrado</TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>{" "}
      <DeleteModal
        open={!!deleteId}
        onClose={() => setDeleteId(undefined)}
        loading={deleteLoading}
        onConfirm={async () => {
          await deleteProduct({ variables: { uuid: deleteId! } });
          setDeleteId(undefined);
          refetch();
        }}
      />
    </>
  );
};
